import * as React from "react";
import { useNavigate } from "react-router-dom";
import { parseResetToken } from "@domain/Auth";
import { useAuthUseCases } from "@gui/contexts/useCases/UseCasesContext";
import { Oops } from "@gui/infrastructure/pass-through/oops";
import { PasswordResetProps, PasswordResetViewModel } from "./passwordreset.types";
import { LogData } from "@rogoag/shared-ts";
import { logger } from "@gui/infrastructure/pass-through/logger";
import { EMAIL_PATTERN, PASSWORD_MIN_LENGTH } from "../common";

// oxlint-disable-next-line no-unused-vars
export function usePasswordResetViewModel(props: PasswordResetProps): PasswordResetViewModel {
	const { passwordReset } = useAuthUseCases();
	const token = parseResetToken(window.location.hash);

	// State
	const [email, setEmail] = React.useState("");
	const [password, setPassword] = React.useState("");
	const [confirmPassword, setConfirmPassword] = React.useState("");
	const [loading, setLoading] = React.useState(false);
	const [emailError, setEmailError] = React.useState("");
	const [passwordError, setPasswordError] = React.useState("");
	const [confirmPasswordError, setConfirmPasswordError] = React.useState("");
	const [error, setError] = React.useState("");
	const [isTokenValid, setIsTokenValid] = React.useState(false);
	const navigate = useNavigate();

	// Token validation effect
	React.useEffect(() => {
		async function checkToken() {
			try {
				const result = await passwordReset.validateToken(token);
				if (result.ok) {
					setEmail(result.value.email);
					setIsTokenValid(true);
				} else {
					setIsTokenValid(false);
				}
			} catch (error: any) {
				setIsTokenValid(false);
				Oops.handleError(error, "Authentication.PasswordReset.checkToken", email);
			}
		}
		checkToken();
	}, []);

	// Email change handler
	const handleEmailChange = (event: { target: { value: React.SetStateAction<string> } }) => {
		setEmailError("");
		// Format-check only what the user actually typed. An empty field is "not filled in
		// yet", not "invalid" — emptiness is enforced on submit. Flagging it here also made
		// the error show on a pristine page: under StrictMode the autoFocus remount blurs
		// the empty field before the user touches anything.
		if (event.target.value !== "" && !EMAIL_PATTERN.test(event.target.value.toString())) {
			setEmailError("Invalid email address");
		}

		setEmail(event.target.value);
	};

	// Password change handler
	const handlePasswordChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
		setPasswordError("");
		const password = event.target.value;
		if (password == "") {
			setPasswordError("Password cannot be empty");
			return;
		}

		if (password.length < PASSWORD_MIN_LENGTH) {
			setPasswordError(`Password must be at least ${PASSWORD_MIN_LENGTH} characters`);
			return;
		}

		setPassword(password);
		if (password != confirmPassword) {
			setPasswordError("Passwords do not match");
			return;
		}
	};

	// Confirm password change handler
	const handleConfirmPasswordChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
		setConfirmPasswordError("");
		const confirmPassword = event.target.value;
		if (confirmPassword == "") {
			setConfirmPasswordError("Password cannot be empty");
			return;
		}

		if (confirmPassword.length < PASSWORD_MIN_LENGTH) {
			setConfirmPasswordError(`Password must be at least ${PASSWORD_MIN_LENGTH} characters`);
			return;
		}

		setConfirmPassword(confirmPassword);
		if (confirmPassword != password) {
			setConfirmPasswordError("Passwords do not match");
			return;
		}
	};

	// Password reset request handler
	const handlePasswordReset = async (event: React.ChangeEvent<HTMLFormElement>) => {
		const logData: LogData = {
			function: "passwordreset.viewmodel.handlePasswordReset",
			email: email,
		};
		event.preventDefault();
		setLoading(true);
		// Emptiness is deliberately not flagged on blur (see handleEmailChange) — this is
		// where it's enforced.
		if (!email) {
			setEmailError("Email cannot be empty");
			setLoading(false);
			return;
		}
		if (emailError) {
			setLoading(false);
			return;
		}
		setError("");

		try {
			logger.log("Sending password reset email", logData);
			await passwordReset.requestReset(email);
			alert("Check your email for a link to reset your password.");
			navigate("/login");
		} catch (error: any) {
			Oops.handleError(error, logData.function, email);
		} finally {
			setLoading(false);
		}
	};

	// Password update handler
	const handlePasswordUpdate = async (event: React.ChangeEvent<HTMLFormElement>) => {
		const logData: LogData = {
			function: "passwordreset.viewmodel.handlePasswordUpdate",
			email: email,
		};
		event.preventDefault();
		setLoading(true);
		if (!email) {
			setEmailError("Email cannot be empty");
			setLoading(false);
			return;
		}
		if (emailError) {
			setLoading(false);
			return;
		}
		setError("");

		try {
			logger.log("Attempting password update", logData);
			const result = await passwordReset.setNewPassword(email, password, token);
			if (result.ok) {
				logger.log("Password update successful", logData);
				alert("Your password has been updated.");
				navigate("/login");
				return;
			}
			logger.warning(`Password update failed: ${result.error}`, logData);
			switch (result.error) {
				case "token_no_longer_valid":
					setError("Your reset link has expired. Please request a new one.");
					break;
			}
		} catch (error: any) {
			Oops.handleError(error, logData.function, email);
		} finally {
			setLoading(false);
		}
	};

	return {
		email,
		password,
		confirmPassword,
		loading,
		emailError,
		passwordError,
		confirmPasswordError,
		error,
		isTokenValid,
		token,
		handleEmailChange,
		handlePasswordChange,
		handleConfirmPasswordChange,
		handlePasswordReset,
		handlePasswordUpdate,
	};
}
